/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable no-nested-ternary */

import React, {
    useEffect, createRef, useRef, useState, useContext,
} from 'react';
import {
    number, object, string, array, bool, shape, arrayOf,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useUIDSeed } from 'react-uid';
import LazyLoad from 'react-lazyload';
import { Paper } from '@material-ui/core';
import Slider from 'react-slick';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import pmallHelper from '../../../../../../../helpers/pmall';
import VideoComponent from '../../VideoComponent';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import SnipeRenderQuery from '../../../common/SnipeRenderQuery';
import { getBrand, getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { sliderFocusOnTouch } from '../../../../../../../helpers/common/helper';
import GraphqlUpdateProductImageSlider from '../../GraphqlUpdateProductImageSlider';

const slideImageUI = ({
    classes, productName, path, name, snipe, snipeImagePath, pmallSummaryInfo, product, snipeImageShow, // changed to destructive object to avoid conflicting with positioning args
}) => {
    const responsiveImagePath = () => {
        const extension = name.includes('_gif') ? '.gif' : 'x.jpg';
        return `${path + name + extension}?height=755&width=690`;
    };
    let imagePath = responsiveImagePath();
    let isExternalSource = false;

    if (name === pmallSummaryInfo?.customizations?.imageURL) {
        imagePath = pmallHelper.buildURL(pmallSummaryInfo, true);
        isExternalSource = true;
    }

    return (
        <div className={classes.imageContainer}>
            <ResponsiveImage
                alt={productName}
                path={imagePath}
                fullWidth
                className={classes.imageWrapper}
                isExternalSource={isExternalSource}
                params={{ desktop: 'quality=100', mobile: 'quality=100', tablet: 'quality=100' }}
            />
            {(snipe && snipeImageShow) && <img className={classes.snipe} alt={snipe} src={`${snipeImagePath}${snipe}_z.gif`} />}
            {snipeImageShow &&  <SnipeRenderQuery  partNumber={product?.partNumber || product?.id}  productType="product" />}
        </div>
    );
};

const slideVideoImage = (classes, videoBlock) => (
    <div>
        <VideoComponent
            classes={classes}
            videoUrl={videoBlock?.video?.url}
            previewImage={videoBlock?.static_image_preview?.url || ''}
            videoClass={classes.productVideo}
            contentType={videoBlock?.video?.content_type || ''}
            videoBlock={videoBlock}
        />
    </div>
);

const ProductImageUIMobileBlock = ({
    classes,
    productName,
    product,
    name,
    path,
    snipe,
    snipeImagePath,
    additionalImages,
    productNameDisplay,
    pmallSummaryInfo,
    videoBlock,
    selectedSku,
    isPdpRedesignEnabled,
    showProductTitleTop,
    isPdpRedesignShortEnabled,
}) => {
    const containerRef = createRef();
    useEffect(() => {
        sliderFocusOnTouch(containerRef);
    });

    const seed = useUIDSeed();
    const slider = useRef();
    const {  presentation_family: presentationFamily = '', featureFlags = {} } = useContext(CrossMerch);
    const brand = useSelector(getBrand);
    const isBRY = brand?.code?.toLowerCase() === 'bry';
    const [previousIndex, setPreviousIndex] = useState(null);
    const altImageArray = [];
    const ffIsAltImagesEnabled = useSelector(getFeatureFlag('is-alt-images-enabled'));
    const dispatch = useDispatch();

    // TODO Chnage to productNameDisplay === "Overlay", Once the value is updated on 18F
    const isProductNameOnOverlay = isPdpRedesignEnabled ? false : productNameDisplay !== 'Normal';
    let primaryImageName = name;
    let additionalImageData = [...additionalImages] ?? [];

    const hasPmallData = pmallSummaryInfo?.customizations?.refnum && pmallSummaryInfo?.customizations?.refnum !== '';

    const isPmallPersonalizationAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'is_pmall_personalization_enabled');
    const isPmallPersonalizationEnabled = isPmallPersonalizationAttr?.values?.[0]?.value === 'true';

    /**
     * if pmall data is present it means there is a preview we want
     * to show first in the slide show. Set what was to be the
     * primary (first) image as the first additional image
     * and set the preview to be primary.
     */
    if (hasPmallData) {
        // capture whats was supposed to be the pirmary image and place it sencond
        additionalImageData = [primaryImageName, ...additionalImageData];
        // make the primary image the preview
        primaryImageName = pmallSummaryInfo.customizations.imageURL;
    }
    let rootContainer = '';
    let productNameStyle = '';
    if (showProductTitleTop) {
        rootContainer = `${classes.rootWrapper} ${classes.sliderWrapper}`;
    } else if (isPdpRedesignEnabled) {
        rootContainer = classes.rootWrapper;
        productNameStyle = `${classes.productNamePdp} ${isPdpRedesignShortEnabled ? classes.pdpShortStyle : ''}`;
    }

    altImageArray.push(slideImageUI({
        classes, productName, path, name: primaryImageName, snipe, snipeImagePath, pmallSummaryInfo, product, snipeImageShow: true,
    }));

    if (ffIsAltImagesEnabled) {
        if (product?.brandId === '1030' || product?.brandId === '1033' || isPmallPersonalizationEnabled) {
            if (additionalImageData !== null && additionalImageData?.length > 0) {
                    additionalImageData?.map((item) => {
                        altImageArray.push(
                            <LazyLoad key={seed(item)}>
                                { slideImageUI({
                                    classes, productName, path, name: item, snipe, snipeImagePath, pmallSummaryInfo, product, snipeImageShow: false,
                                })}
                            </LazyLoad>,
                        );
                    });
            } else {
                    selectedSku?.image?.productThumbnails?.map((item) => {
                        altImageArray.push(
                            <LazyLoad key={seed(item)}>
                                { slideImageUI({
                                    classes, productName, path: item?.path, name: item?.name, snipe, snipeImagePath, pmallSummaryInfo, product, snipeImageShow: false,
                                }) }
                            </LazyLoad>,
                        );
                    });
            }
        }

        if (videoBlock?.enabled && presentationFamily === 'flower' && !isBRY) {
            altImageArray.push(slideVideoImage(classes, videoBlock));
        }

        if (additionalImageData?.length > 0 && product?.brandId !== '1030' && product?.brandId !== '1033' && !isPmallPersonalizationEnabled) {
            additionalImageData.forEach((img) => {
                altImageArray.push(
                    <LazyLoad key={seed(img)}>

                        {slideImageUI({
                            classes, productName, path, name: img, snipe, snipeImagePath, pmallSummaryInfo, product, snipeImageShow: false,
                        }) }
                    </LazyLoad>,
                );
            });
        }

        if (videoBlock?.enabled && presentationFamily === 'flower' && isBRY) {
            altImageArray.push(slideVideoImage(classes, videoBlock));
        }
    }

    // Ga tagging event function
    const trackEventClick = (item, count) => {
        dispatch(trackEvent({
            eventCategory: 'product image',
            eventAction: count > previousIndex ?   'swiped right' : 'swiped left',
            eventLabel: `${product?.baseCode} | ${item} | ${count + 1}`,
        }));
    };

    const track = (value) => {
        let imageUrl = '';
        setPreviousIndex(value);
        const slideElement = slider.current.innerSlider.list.querySelector(`[data-index="${value}"]`);
        let vid = '';
        if (typeof document !== 'undefined' && isBRY) {
            vid = document?.getElementById('productVideo');
        }
        if (altImageArray.length > 0) {
            if (slideElement?.getElementsByTagName('video')?.[0]) { // for video
                if (vid) {
                    vid.play();
                }
                imageUrl =  slideElement.getElementsByTagName('video')[0].poster;
                trackEventClick(imageUrl, value);
            } else if (slideElement?.getElementsByTagName('img')?.[0]) {
                imageUrl = new URL(slideElement?.getElementsByTagName('img')[0]?.src);
                imageUrl = imageUrl.origin + imageUrl.pathname;
                trackEventClick(imageUrl, value);
                if (vid) {
                    vid.pause();
                }
            }
        }
    };
    const productNameContainer = () => (
        !ffIsAltImagesEnabled ? null : altImageArray?.length > 1 ? (
            <div className={isProductNameOnOverlay ? `${classes.productNameOnOverlay}` : ''}>
                {isProductNameOnOverlay ?  (
                    <div className={` ${classes.productNameContainer}`}>
                        <div className={classes.productName}>{productName}</div>
                    </div>
                ) : null}
            </div>

        ) : (
            isProductNameOnOverlay &&   (
                <div className={`${classes.productNameContainer} ${classes.singleProduct}`} data-testId="anc">
                    <div className={classes.productName}>{productName}</div>
                </div>
            )

        )
    );
    return (
        <>
            {featureFlags['is-updated-mobile-pdp-slider']
                ? (
                    <GraphqlUpdateProductImageSlider
                        classes={classes}
                        containerRef={containerRef}
                        altImageArray={altImageArray}
                        track={track}
                        isProductNameOnOverlay={isProductNameOnOverlay}
                        ffIsAltImagesEnabled={ffIsAltImagesEnabled}
                        slider={slider}
                        productNameContainer={productNameContainer}
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                        showProductTitleTop={showProductTitleTop}
                        isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                    />
                )
                : (
                    <Paper ref={containerRef} className={`${classes.root} ${rootContainer}`}>
                        <Slider
                            dots
                            accessibility
                            speed={500}
                            infinite={false}
                            slidesToShow={1}
                            slidesToScroll={1}
                            arrows={false}
                            cssEase="linear"
                            ref={slider}
                            className={`carousel-container ${classes.pdpCarouselContainer} ${isProductNameOnOverlay && altImageArray.length > 1  ? classes.slickOverlay : ''} ${ altImageArray.length > 1 ? classes.pdpAltScrollBar : ''}`}
                            dotsClass="slick-dots slick-thumb"
                            customPaging={(i) => <div className={classes.pdpDotNavigation} aria-label={`Image ${i + 1}`} />}
                            afterChange={(current) => { track(current); }}
                        >
                            {altImageArray?.map((item) => (
                                <div key={seed(item)}>
                                    { item }
                                </div>
                            ))}
                        </Slider>
                        {!showProductTitleTop && productNameContainer()}
                    </Paper>
                )}
            {!isProductNameOnOverlay && !showProductTitleTop ? <div className={`${classes.productNameforNormal} ${productNameStyle}`} aria-hidden="true">{productName}</div> : null}
        </>
    );
};

ProductImageUIMobileBlock.propTypes = {
    classes: object.isRequired,
    product: shape({
        baseCode: string.isRequired,
    }).isRequired,
    productName: arrayOf(string.isRequired).isRequired,
    path: string.isRequired,
    snipe: string.isRequired,
    name: string.isRequired,
    snipeImagePath: string.isRequired,
    additionalImages: array.isRequired,
    productNameDisplay: string.isRequired,
    pmallSummaryInfo: shape({
        customization: shape({
            count: number,
            refnum: string,
            mobileURL: string,
            imageURL: string,
        }),
    }),
    videoBlock: shape({
        enabled: bool,
        static_image_preview: shape({
            url: string,
        }),
        video: shape({
            url: string,
            content_type: string,
        }),
    }),
    selectedSku: shape({
        image: shape({
            productThumbnails: shape({
                name: string.isRequired,
                path: string.isRequired,
            }),
        }),
    }),
    isPdpRedesignEnabled: bool,
    showProductTitleTop: bool,
    isPdpRedesignShortEnabled: bool,
};

ProductImageUIMobileBlock.defaultProps = {
    pmallSummaryInfo: {
        constomizations: {
            count: 0,
            refnum: '',
            mobileURL: '',
            imageURL: '',
        },
    },
    videoBlock: {
        enabled: false,
        static_image_preview: {
            url: '',
        },
        video: {
            url: '',
            content_type: '',
        },
    },
    selectedSku: {},
    isPdpRedesignEnabled: false,
    showProductTitleTop: false,
    isPdpRedesignShortEnabled: false,
};

export default ProductImageUIMobileBlock;
